// src/pages/ValveProfilePage.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axiosInstance';
import '../Components/EquipmentProfilePage.css';
import '../Components/ProfileWorkspaceTheme.css';
import { useNavigate, useParams } from 'react-router-dom';
import { isAuthenticated as userIsAuthenticated } from '../auth/cookieUtils';
import { apiFileUrl } from '../config/runtimeConfig';

const resolveFileUrl = apiFileUrl;

const formatDate = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val; // LocalDate usually "YYYY-MM-DD"
  try {
    return new Date(val).toISOString().slice(0, 10);
  } catch {
    return String(val);
  }
};

const displayValue = (val) => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'string') {
    const trimmed = val.trim();
    return trimmed ? trimmed : '—';
  }
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
};

const ValveProfilePage = () => {
  const { valveId } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = userIsAuthenticated();

  const [valve, setValve] = useState(null);
  const [docs, setDocs] = useState([]);
  const [expandedImage, setExpandedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Optional enrichment if ValveDTO doesn’t include location/equipment/area friendly names
  const [equipmentContext, setEquipmentContext] = useState(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const [valveRes, docsRes] = await Promise.all([
          axios.get(`/api/valves/${valveId}`),
          axios.get(`/api/valves/${valveId}/documents`).catch(() => ({ data: [] })),
        ]);

        if (!mounted) return;

        const valveData = valveRes.data;
        setValve(valveData);
        setDocs(docsRes.data || []);

        const equipmentId = valveData?.equipmentId;
        const needsContext =
          equipmentId &&
          (!valveData?.locationName || !valveData?.equipmentName || !valveData?.areaName);

        if (needsContext) {
          try {
            const eqRes = await axios.get(`/api/equipment/${equipmentId}/profile`);
            if (!mounted) return;

            const profile = eqRes.data || {};
            setEquipmentContext({
              locationName:
                profile?.equipmentDescription?.locationName ||
                profile?.locationName ||
                '',
              equipmentNumber:
                profile?.equipmentDescription?.equipmentNumber ||
                profile?.equipmentNumber ||
                '',
              equipmentName:
                profile?.equipmentDescription?.name ||
                profile?.equipmentDescription?.equipmentName ||
                profile?.equipmentName ||
                '',
              areaName:
                profile?.equipmentDescription?.areaName ||
                profile?.areaName ||
                '',
              sapNumber:
                profile?.equipmentDescription?.sapNumber ||
                profile?.sapNumber ||
                '',
            });
          } catch {
            setEquipmentContext(null);
          }
        } else {
          setEquipmentContext(null);
        }
      } catch (err) {
        console.error('Error loading valve profile:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [valveId]);


  const valvePhotoPath =
    valve?.photoPath || valve?.valvePhotoPath || valve?.imagePath || valve?.photoFilePath || null;

  const orderedFields = useMemo(() => {
    const locationName = valve?.locationName ?? equipmentContext?.locationName;
    const equipmentNumber = valve?.equipmentNumber ?? equipmentContext?.equipmentNumber;
    const equipmentName = valve?.equipmentName ?? equipmentContext?.equipmentName;
    const areaName = valve?.areaName ?? equipmentContext?.areaName;
    const sapNumber = valve?.sapNumber ?? equipmentContext?.sapNumber;

    return [
      { label: 'Valve Tag Number', value: valve?.valveTagNumber },
      { label: 'Location', value: locationName },
      { label: 'Equipment Number', value: equipmentNumber },
      { label: 'Parent Equipment Name', value: equipmentName },
      { label: 'Area', value: areaName },
      { label: 'SAP Number', value: sapNumber },
      { label: 'Set Pressure', value: valve?.setPressure },
      { label: 'Test Frequency', value: valve?.testFrequency },
      { label: 'Last Test Date', value: formatDate(valve?.lastTestDate) },
      { label: 'Next Test Date', value: formatDate(valve?.nextTestDate) },
      { label: 'Last Repair/Replacement', value: formatDate(valve?.lastRepairReplacement) },
      { label: 'Next Repair/Replacement', value: formatDate(valve?.nextRepairReplacement) },
      { label: 'Repair Frequency', value: valve?.repairFrequency },
      { label: 'Size', value: valve?.size },
      { label: 'Required Capacity', value: valve?.requiredCapacity },
      { label: 'Valve Capacity', value: valve?.valveCapacity },
      { label: 'Contents', value: valve?.contents },
      { label: 'Type', value: valve?.type },
      { label: 'Manufacturer', value: valve?.manufacturer },
      { label: 'Model Number', value: valve?.modelNumber },
      { label: 'Serial Number', value: valve?.serialNumber },
      { label: 'Valve Flange Rating', value: valve?.valveFlangeRating },
      { label: 'Valve Notes', value: valve?.valveNotes },
    ];
  }, [valve, equipmentContext]);

  if (loading) {
    return (
      <div className="equipment-profile-page odin-profile-page">
        <div className="profile-loading-state" role="status">
          <span className="profile-loading-indicator" aria-hidden="true" />
          <h1>Loading valve profile</h1>
          <p>Retrieving the valve record and inspection documents.</p>
        </div>
      </div>
    );
  }

  if (!valve) {
    return (
      <div className="equipment-profile-page odin-profile-page">
        <div className="profile-loading-state profile-not-found-state">
          <span className="profile-page-eyebrow">Valve record</span>
          <h1>Valve not found</h1>
          <p>The requested valve record could not be loaded.</p>
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="equipment-profile-page odin-profile-page valve-profile-page">
        {/* Header bar (matches EquipmentProfilePage theme) */}
        <div className="profile-header-bar">
          <div className="profile-header-left">
            {isAuthenticated && (
              <button type="button" className="back-btn" onClick={() => navigate(-1)}>
                ← Back
              </button>
            )}
          </div>

          <div className="profile-header-center">
            <div className="title-photo-wrapper">
              <div className="profile-title-copy">
                <span className="profile-page-eyebrow">Pressure relief device</span>
                <h1 className="slideout-title">Valve Profile</h1>
                <span className="profile-page-subtitle">
                  {displayValue(valve.valveTagNumber)}
                </span>
              </div>
            </div>
          </div>

          <div className="profile-header-right">
            <button type="button" className="print-btn" onClick={() => window.print()}>
              🖨️ Print
            </button>
          </div>
        </div>

        <main className="slide-content profile-workspace-content">

         

          <div className="slideout-grid">
            {/* Valve details (manual ordered lines) */}
            <div className="profile-section-card">
              <h3 className="section-title">Valve Details</h3>

         {valvePhotoPath && (
  <div className="profile-photo-row">
    <div className="design-photo-inline">
      <img
        src={resolveFileUrl(valvePhotoPath)}
        alt="Valve Preview"
        onClick={() => setExpandedImage(resolveFileUrl(valvePhotoPath))}
        className="design-preview-img"
      />
    </div>
  </div>
)}

              {orderedFields.map((row) => (
  <p key={row.label}>
    <strong>{row.label}:</strong>{' '}
    {row.label === 'Parent Equipment Name' && valve?.equipmentId ? (
      <button
        type="button"
        className="table-link profile-parent-link"
        onClick={() => navigate(`/equipment/${valve.equipmentId}/profile`)}
      >
        {displayValue(row.value)}
      </button>
    ) : (
      displayValue(row.value)
    )}
  </p>
))}
            </div>

            {/* Valve documents */}
            <div className="profile-section-card files-section">
              <h3 className="section-title">Valve Inspection Documents</h3>

              {docs?.length > 0 ? (
                <div className="file-list profile-file-list">
                  {docs.map((doc, idx) => (
                    <div className="file-item" key={doc.id ?? idx}>
                      <a
                        className="file-name"
                        href={resolveFileUrl(doc.filePath)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {doc.displayName?.trim() ||
                          doc.fileName?.trim() ||
                          doc.filePath?.split('/').pop() ||
                          `Document ${idx + 1}`}
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="hint profile-empty-copy">
                  No valve inspection documents uploaded.
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Image modal (same as equipment page) */}
      {expandedImage && (
        <div className="image-modal profile-theme-modal" onClick={() => setExpandedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="close-modal" onClick={() => setExpandedImage(null)}>
              ✕
            </button>
            <img src={expandedImage} alt="Expanded Preview" />
          </div>
        </div>
      )}
    </>
  );
};

export default ValveProfilePage;
